import React, { useState, useRef, useLayoutEffect, useEffect, useId } from 'react'
import { tv } from 'tailwind-variants'
import Menu, { MenuProps } from 'components/menu'
import { FixedSizeList } from 'react-window'
import Button from 'components/button'
import { SelectProps } from 'components/select'
import { OptionProps } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { matchesAllTokens } from 'helpers/search'
import { Delete, Plus, Tick } from 'components/icons'
import { MenuSearch } from 'components/menu/items'
import { textWidth } from 'helpers/textWidth'
import Spinner from 'components/spinner'
import Tooltip from 'components/tooltip'

// In the manner of shadcn/ui's command menu, and set as the app's menus set their rows (MenuItem):
// a soft, rounded search field with its icon inside, inset in the menu; 32px rounded rows of 13px
// medium text with a 16px icon, the highlighted row on the menu rows' hover fill, and a tick on
// what's selected. Group labels are small and muted.
const OPTION_HEIGHT = 32
const VISIBLE_OPTIONS = 8
const LIST_PADDING = 4

const optionStyles = tv({
  base: 'flex items-center gap-2 px-2 rounded-md text-[13px] text-body outline-none',
  variants: {
    // A menu's rows are medium; a field's list is set as the field's own text is.
    weight: {
      medium: 'font-medium',
      normal: 'font-normal'
    },
    // A group's heading, as the menus' MenuLabel is set: 12px medium and muted, just over its rows. Not the
    // first, a hairline runs across the menu over it, as MenuSeparator sets groups apart.
    groupLabel: {
      true: 'items-end pb-1 text-xs font-medium text-muted cursor-default select-none',
      false: 'cursor-pointer transition-colors'
    },
    separated: {
      true: 'before:absolute before:-inset-x-1 before:top-1 before:h-px before:bg-divider',
      false: ''
    },
    focused: {
      true: 'bg-surface-hover',
      false: ''
    },
    // Faded, and not clickable-looking: no pointer and no highlight.
    disabled: {
      true: 'text-faint cursor-default'
    }
  }
})

interface SelectMenuProps extends SelectProps, MenuProps {
  /** The options' weight: medium, as a menu's rows are, or normal, as a field's text is (a form's dropdown). */
  optionWeight?: 'medium' | 'normal'
}

// A list is as wide as its longest option needs (of the first few hundred), with the list's inset, the row's
// padding, an icon and the tick around it, and never narrower than it was asked to be (its trigger's width);
// up to a point, past which an option is cut short, with the whole of it on hover.
const FITTED_MENU_MAX = 480
const FITTED_OPTION_CHROME = 80
const OPTION_FONT = '500 13px Inter'
// How many options the width is measured from.
const FITTED_SAMPLE = 500
// The labels the width is measured from, as one string: the width is measured again whenever any of them changes.
const fittedLabels = (options: OptionProps[]): string =>
  options
    .slice(0, FITTED_SAMPLE)
    .map((option) => String(option.label ?? ''))
    .join('\u0000')
export const fittedMenuWidth = (options: OptionProps[]): number =>
  Math.min(
    FITTED_MENU_MAX,
    Math.ceil(
      options
        .slice(0, FITTED_SAMPLE)
        .reduce((widest, option) => Math.max(widest, textWidth(String(option.label ?? ''), OPTION_FONT)), 0) +
        FITTED_OPTION_CHROME
    )
  )

const SelectMenu: React.FC<SelectMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  options,
  onOptionClick,
  loading,
  multiselect,
  optionsSelected,
  setOptionsSelected,
  groupBy,
  customAddText,
  customRemoveText,
  noOptionsMessage,
  searchable = true,
  onCreateOption,
  onAddAll,
  searchPlaceholder = 'Search',
  optionWeight = 'medium'
}) => {
  // Measured as the list opens, and again only when its options change (they can still be loading), not on every
  // keystroke in the search or arrow key through the list.
  const optionsKey = fittedLabels(options)
  const [fittedWidth, setFittedWidth] = useState<number>(() => fittedMenuWidth(options))
  useEffect(() => {
    setFittedWidth(fittedMenuWidth(options))
  }, [optionsKey])
  const listWidth = width === undefined ? undefined : Math.max(width, fittedWidth)
  const baseId = useId()
  const [search, setSearch] = useState<string>('')
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const listRef = useRef<FixedSizeList>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)

  const groupSelected = (list: Array<OptionProps>, keyGetter: (input: OptionProps) => string) => {
    const map = new Map()
    list.forEach((item) => {
      const key = keyGetter(item)
      const collection = map.get(key)
      if (!collection) {
        map.set(key, [item])
      } else {
        collection.push(item)
      }
    })
    return map
  }

  const getGroupArray = (groups: Map<string, OptionProps[]>) => {
    const array: Array<OptionProps> = []

    // 1) Ungrouped options (key === '') go first (no header)
    const ungrouped = groups.get('') || []
    ungrouped.forEach((option) => array.push(option))

    // 2) Then all named groups with a header
    groups.forEach((object, key: string) => {
      if (key === '') return // already handled
      array.push({ label: key }) // group label row (no value)
      object.forEach((option) => array.push(option))
    })

    return array
  }

  const filteredOptions = options
    .filter((option: OptionProps) => matchesAllTokens(option, search))
    .sort((a, b) => {
      // Sort options by which ones are selected ONLY on first load or when searching
      // (dont change sort order when selecting as this is bad behaviour)
      if (multiselect && Array.isArray(optionsSelected) && optionsSelected.length > 0 && a.value && b.value) {
        if (optionsSelected.indexOf(a.value) !== -1 && optionsSelected.indexOf(b.value) === -1) {
          return -1
        } else if (optionsSelected.indexOf(a.value) === -1 && optionsSelected.indexOf(b.value) !== -1) {
          return 1
        } else {
          return 0
        }
      } else {
        return 0
      }
    })

  const groups = groupBy ? groupSelected(filteredOptions, (o) => (o.group ? o.group.trim() : '')) : undefined
  const finalArray = groups ? getGroupArray(groups) : filteredOptions

  const trimmedSearch = search.trim()
  const showCreateOption =
    !!onCreateOption &&
    !loading &&
    trimmedSearch.length > 0 &&
    !options.some((option) => (option.label ?? '').trim() === trimmedSearch)

  // The create row is a navigable item rendered after the options, so it occupies
  // the index just past the last option. Keyboard nav treats it like any option.
  const createOptionIndex = finalArray.length

  const handleCreateOption = () => {
    if (!onCreateOption || !showCreateOption) return
    onCreateOption(trimmedSearch)
    if (!multiselect) {
      setMenuState(INITIAL_CONTEXT_MENU_STATE)
    } else {
      setSearch('')
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }

  // What the add-all row takes: every option the list shows that can be chosen and isn't already. Offered only when
  // that's more than one, as otherwise it would do just what clicking the one option does.
  const addableValues = onAddAll
    ? filteredOptions
        .filter((option) => option.value !== undefined && !option.disabled && !optionsSelected?.includes(option.value))
        .map((option) => option.value as string)
    : []
  const showAddAll = !loading && addableValues.length > 1
  // The add-all row is kept at the foot of the list, under the options and the create row, so it comes last as the
  // arrows go. It's never where the keyboard starts: Enter straight after a search still takes the first match.
  const addAllIndex = finalArray.length + (showCreateOption ? 1 : 0)
  const addAllLabel = search.trim()
    ? `Add all ${addableValues.length.toLocaleString()} matching`
    : `Add all ${addableValues.length.toLocaleString()} values`

  const handleAddAll = () => {
    if (!onAddAll || !showAddAll) return
    onAddAll(addableValues)
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
  }

  // Where the keyboard starts in the list: on the option chosen, in a list that picks one with nothing searched, so
  // the arrows move on from it; otherwise on the first that can be chosen (not a group's heading or a disabled one).
  const startIndex = (): number => {
    const chosen =
      !multiselect && !search
        ? finalArray.findIndex(
            (option) => option.value !== undefined && !option.disabled && option.value === optionsSelected?.[0]
          )
        : -1
    return chosen >= 0 ? chosen : finalArray.findIndex((option) => option.value !== undefined && !option.disabled)
  }

  useLayoutEffect(() => {
    const start = startIndex()
    setFocusedIndex(start)
    if (start > 0) listRef.current?.scrollToItem(start, 'smart')
    if (searchRef.current) {
      searchRef.current.focus()
    } else if (listboxRef.current) {
      listboxRef.current.focus()
    } else if (containerRef.current) {
      containerRef.current.focus()
    }
  }, [])

  // When the option set changes (e.g. the user types), focus the first option, or
  // nothing when there are no matches. This keeps the create row from ever being
  // focused by default, so Enter/Tab only creates an option once the user has
  // navigated onto the "+" row
  const matchCount = filteredOptions.length
  useEffect(() => {
    setFocusedIndex(startIndex())
  }, [search, matchCount])

  // A navigable index is an option (not a group label), the create row or the add-all row.
  const isSelectableIndex = (index: number): boolean => {
    if (showCreateOption && index === createOptionIndex) return true
    if (showAddAll && index === addAllIndex) return true
    // Not a group's heading, and not an option that can't be chosen.
    return (
      index >= 0 && index < finalArray.length && finalArray[index].value !== undefined && !finalArray[index].disabled
    )
  }

  const findNextSelectableIndex = (currentIndex: number, direction: 'next' | 'prev'): number => {
    const navLength = finalArray.length + (showCreateOption ? 1 : 0) + (showAddAll ? 1 : 0)
    if (navLength === 0) return -1
    let nextIndex = currentIndex
    const step = direction === 'next' ? 1 : -1

    for (let i = 0; i < navLength; i++) {
      nextIndex += step

      // Wrap around
      if (nextIndex < 0) nextIndex = navLength - 1
      if (nextIndex >= navLength) nextIndex = 0

      if (isSelectableIndex(nextIndex)) {
        return nextIndex
      }
    }
    return -1 // No selectable item found
  }

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!menuState.open || (finalArray.length === 0 && !showCreateOption)) return
    // Home and End go to the first and last option, unless there's a search to move the caret through.
    if ((event.key === 'Home' || event.key === 'End') && !search) {
      event.preventDefault()
      event.stopPropagation()
      const edge = findNextSelectableIndex(
        event.key === 'Home' ? -1 : finalArray.length,
        event.key === 'Home' ? 'next' : 'prev'
      )
      setFocusedIndex(edge)
      if (listRef.current && edge >= 0 && edge < finalArray.length) listRef.current.scrollToItem(edge, 'smart')
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== 'Tab') return

    const isSelectableOption = (index: number) => {
      return index >= 0 && index < finalArray.length && finalArray[index].value !== undefined
    }

    event.preventDefault()
    event.stopPropagation()

    switch (event.key) {
      case 'ArrowDown': {
        const nextIndex = findNextSelectableIndex(focusedIndex, 'next')
        setFocusedIndex(nextIndex)
        // The create row lives outside the virtualized list, so only scroll for options.
        if (listRef.current && nextIndex >= 0 && nextIndex < finalArray.length) {
          listRef.current.scrollToItem(nextIndex, 'smart')
        }
        break
      }
      case 'ArrowUp': {
        const prevIndex = findNextSelectableIndex(focusedIndex, 'prev')
        setFocusedIndex(prevIndex)
        if (listRef.current && prevIndex >= 0 && prevIndex < finalArray.length) {
          listRef.current.scrollToItem(prevIndex, 'smart')
        }
        break
      }
      case 'Tab':
      case 'Enter':
        // Only create when the user has actually navigated onto the create row —
        if (showCreateOption && focusedIndex === createOptionIndex) {
          handleCreateOption()
        } else if (showAddAll && focusedIndex === addAllIndex) {
          handleAddAll()
        } else if (isSelectableOption(focusedIndex)) {
          const focusedValue = finalArray[focusedIndex].value
          if (focusedValue != null && !finalArray[focusedIndex].disabled) {
            onOptionClick(focusedValue)
            if (!multiselect) {
              setMenuState(INITIAL_CONTEXT_MENU_STATE)
            } else {
              // Refocus search bar after selecting option in multiselect mode
              setTimeout(() => {
                searchRef.current?.focus()
              }, 0)
            }
          }
        }
        break
    }
  }

  // Focus stays in the search box (or the menu, without one) and points at the highlighted option,
  // so the arrow keys move what's announced along with what's highlighted.
  // Ids of their own rather than built from the menu's, which pages and tests find menus by.
  const listboxId = `${baseId}-listbox`
  const optionId = (index: number): string => `${baseId}-option-${index}`
  const createOptionId = `${baseId}-create-option`
  const addAllId = `${baseId}-add-all`
  // Only ever an option: in a grouped list the highlight can start on a group's heading.
  const activeDescendantOf = (index: number): string | undefined => {
    if (!isSelectableIndex(index)) return undefined
    if (showCreateOption && index === createOptionIndex) return createOptionId
    if (showAddAll && index === addAllIndex) return addAllId
    return optionId(index)
  }
  const activeDescendant = activeDescendantOf(focusedIndex)

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={listWidth} zIndex={3000}>
      <div ref={containerRef} className="flex flex-col focus:outline-none" onKeyDown={handleKeyDown} tabIndex={-1}>
        {searchable && (
          <MenuSearch
            role="combobox"
            aria-expanded={true}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendant}
            placeholder={searchPlaceholder}
            aria-label="Search"
            // Held here, so adding a typed option in a list that stays open empties the field for the next.
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            readOnly={loading}
            ref={searchRef}
          />
        )}

        {/* The padding sits round the list rather than on each row, so a long list scrolls its
            last row fully into view. */}
        <div
          id={listboxId}
          ref={listboxRef}
          className="overflow-hidden py-1 focus:outline-none"
          role="listbox"
          // Without a search box, the list itself takes focus and points at the highlighted option.
          tabIndex={-1}
          aria-activedescendant={searchable ? undefined : activeDescendant}
          aria-multiselectable={multiselect || undefined}
          aria-label="Options"
        >
          {finalArray.length === 0 || loading ? (
            // When the only thing to show would be the create row (rendered below),
            // collapse this box instead of leaving a blank gap.
            loading || !showCreateOption ? (
              // Wraps to the list's width, as the menu around it doesn't: a message can be a sentence or two.
              <div className="flex items-center min-h-10 px-3 py-2.5 text-[13px] leading-5 text-muted whitespace-normal">
                {loading ? (
                  <div className="flex w-full items-center">
                    <Spinner size="small" />
                    <span className="ml-4">Loading Options</span>
                  </div>
                ) : (
                  <span className="min-w-0 text-pretty">{noOptionsMessage}</span>
                )}
              </div>
            ) : null
          ) : (
            <FixedSizeList
              ref={listRef}
              // As tall as the options, up to eight, so a short list doesn't leave a gap below it.
              height={Math.min(finalArray.length, VISIBLE_OPTIONS) * OPTION_HEIGHT}
              itemCount={finalArray.length}
              itemSize={OPTION_HEIGHT}
              width={'100%'}
            >
              {({ index, style }) => {
                const value = finalArray[index].value
                const isGroupLabel = value === undefined
                const selected =
                  value !== undefined && Array.isArray(optionsSelected) && optionsSelected.indexOf(value) !== -1
                const disabled = finalArray[index].disabled === undefined ? false : finalArray[index].disabled
                const isFocused = index === focusedIndex && !isGroupLabel

                return (
                  <div
                    key={index}
                    id={optionId(index)}
                    data-testid="select-menu-option"
                    aria-disabled={disabled || undefined}
                    className={optionStyles({
                      weight: isGroupLabel ? undefined : optionWeight,
                      groupLabel: isGroupLabel,
                      separated: isGroupLabel && index > 0,
                      focused: isFocused && !disabled,
                      disabled
                    })}
                    // Inset from the menu's edges, so the rounded highlight has room around it.
                    style={{ ...style, left: LIST_PADDING, width: `calc(100% - ${LIST_PADDING * 2}px)` }}
                    role={isGroupLabel ? 'presentation' : 'option'}
                    aria-selected={isGroupLabel ? undefined : selected}
                    tabIndex={-1}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (disabled) return
                      if (value != null) {
                        setFocusedIndex(index)
                        onOptionClick(value)
                        if (!multiselect) {
                          setMenuState(INITIAL_CONTEXT_MENU_STATE)
                        }
                      }
                    }}
                    onMouseEnter={() => {
                      if (!isGroupLabel) {
                        setFocusedIndex(index)
                      }
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onBlur={(event) => event.stopPropagation()}
                  >
                    {!isGroupLabel && finalArray[index].icon && (
                      <span className="flex shrink-0">{finalArray[index].icon}</span>
                    )}
                    {disabled && finalArray[index].tooltip ? (
                      <Tooltip content={finalArray[index].tooltip!}>
                        <span className="truncate flex-1 text-faint">{finalArray[index].label}</span>
                      </Tooltip>
                    ) : (
                      // The browser's own title shows a cut-off option in full.
                      <span
                        title={`${finalArray[index].label}`}
                        className={`truncate flex-1 ${disabled ? 'text-faint' : ''}`}
                      >
                        {finalArray[index].label}
                      </span>
                    )}
                    {/* A list whose choices are actions (Hide / Show, Require / Unrequire) says
                        what a click on the row does; any other list ticks what's selected. It's a
                        label shaped like a button, not a button: an option can't hold a control of
                        its own, and the whole row is the click. */}
                    {multiselect && !isGroupLabel && !disabled && (customAddText || customRemoveText) && (
                      <span className="ml-auto inline-flex items-center h-6 px-2 rounded-md border border-divider bg-surface text-[12px] text-body">
                        {selected ? customRemoveText || 'Remove' : customAddText || 'Add'}
                      </span>
                    )}
                    {!isGroupLabel && selected && !(multiselect && (customAddText || customRemoveText)) && (
                      <Tick className="ml-auto shrink-0 size-4 text-body" data-testid="select-menu-option-selected" />
                    )}
                  </div>
                )
              }}
            </FixedSizeList>
          )}
          {showCreateOption && (
            <div
              id={createOptionId}
              role="option"
              aria-selected={false}
              tabIndex={-1}
              data-testid="select-menu-create-option"
              className={optionStyles({
                weight: optionWeight,
                groupLabel: false,
                focused: focusedIndex === createOptionIndex,
                class: 'h-8 m-1'
              })}
              onClick={(event) => {
                event.stopPropagation()
                setFocusedIndex(createOptionIndex)
                handleCreateOption()
              }}
              onMouseEnter={() => setFocusedIndex(createOptionIndex)}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <Plus className="shrink-0 size-4 text-muted" />
              <span className="truncate flex-1">Add “{trimmedSearch}”</span>
            </div>
          )}
          {showAddAll && (
            <>
              {/* Set apart from what's over it, as MenuSeparator sets a menu's groups apart. */}
              <div aria-hidden className="my-1 h-px bg-divider" />
              <div
                id={addAllId}
                role="option"
                aria-selected={false}
                tabIndex={-1}
                data-testid="select-menu-add-all"
                className={optionStyles({
                  weight: optionWeight,
                  groupLabel: false,
                  focused: focusedIndex === addAllIndex,
                  class: 'h-8 mx-1'
                })}
                onClick={(event) => {
                  event.stopPropagation()
                  setFocusedIndex(addAllIndex)
                  handleAddAll()
                }}
                onMouseEnter={() => setFocusedIndex(addAllIndex)}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Plus className="shrink-0 size-4 text-muted" />
                <span className="truncate flex-1">{addAllLabel}</span>
              </div>
            </>
          )}
        </div>

        {/* Only a list that picks several at once has a foot: its count and its all-at-once buttons.
            Any other list is just its options, as a menu is. */}
        {setOptionsSelected && (
          <div className="flex items-center border-t border-divider w-full h-10 px-3 text-[12px]">
            {!loading && <div className="text-muted whitespace-nowrap">{filteredOptions.length} Options</div>}
            {finalArray.length > 0 && setOptionsSelected && (
              <div className="flex w-full gap-1">
                <Button
                  className="ml-auto h-7 px-2 text-[12px] font-normal"
                  internalType="outline"
                  disabled={loading}
                  onClick={() => {
                    if (Array.isArray(optionsSelected)) {
                      setOptionsSelected(
                        optionsSelected.filter(
                          (option) => !filteredOptions.map((option) => option.value).includes(option)
                        )
                      )
                    }
                  }}
                >
                  {customRemoveText ? customRemoveText : <Delete className="size-3.5" />} All
                </Button>
                <Button
                  className="h-7 px-2 text-[12px] font-normal"
                  internalType="outline"
                  disabled={loading}
                  onClick={() => {
                    if (Array.isArray(optionsSelected)) {
                      setOptionsSelected(
                        Array.from(
                          new Set([
                            ...optionsSelected,
                            ...filteredOptions
                              .filter((option) => !option.disabled)
                              .map((option) => (option.value !== undefined ? option.value : ''))
                          ])
                        )
                      )
                    }
                  }}
                >
                  {customAddText ? customAddText : <Plus className="size-3.5" />} All
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Menu>
  )
}

export default SelectMenu
